/**
 * DebugPanel — developer debug overlay (double-click title to toggle).
 * Extracted from Index.tsx for maintainability.
 */

import { motion } from 'framer-motion';
import type { BackendBuddyState, BackendMeta } from '@/hooks/useBuddyBackend';
import type { MacroDisplayBundle } from '@/lib/types/macroDisplay';

interface DebugPanelProps {
  bs: BackendBuddyState;
  meta: BackendMeta;
  macro: MacroDisplayBundle;
  activeTransient: string | null;
  transientDebug: { triggerCount: number };
  onClose: () => void;
  onSimulateBlock: () => void;
}

function DebugRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-[7px] font-body text-muted-foreground/30 uppercase tracking-wider shrink-0">{label}</span>
      <span className="text-[8px] font-body text-muted-foreground/60 text-right truncate">{value}</span>
    </div>
  );
}

function DebugSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <span className="text-[7px] font-pixel uppercase tracking-wider block" style={{ color: 'hsl(var(--bitcoin) / 0.35)' }}>{title}</span>
      {children}
    </div>
  );
}

export default function DebugPanel({
  bs, meta, macro, activeTransient, transientDebug,
  onClose, onSimulateBlock,
}: DebugPanelProps) {
  const adv = macro.advisory;

  return (
    <motion.div data-share-exclude="true"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.3 }}
      className="fixed bottom-0 inset-x-0 z-50 p-3 max-h-[40vh] overflow-y-auto"
      style={{
        background: 'hsl(var(--background) / 0.95)',
        borderTop: '1px solid hsl(var(--border) / 0.3)',
        backdropFilter: 'blur(12px)',
      }}
    >
      <div className="max-w-sm mx-auto space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-[8px] font-pixel text-bitcoin/50 tracking-wider">DEBUG</span>
          <div className="flex items-center gap-2">
            <button
              onClick={onSimulateBlock}
              className="text-[7px] font-body font-medium px-2 py-0.5 rounded bg-bitcoin/20 text-bitcoin/70 hover:bg-bitcoin/30 transition-colors"
            >
              ⛏ Test Block
            </button>
            <button
              onClick={onClose}
              className="text-[8px] font-body text-muted-foreground/40 hover:text-muted-foreground/60"
            >
              close
            </button>
          </div>
        </div>

        {/* Macro v2 section */}
        <DebugSection title="Macro v2">
          <DebugRow label="State" value={macro.state} />
          <DebugRow label="Val score" value={String(adv.valuationScore)} />
          <DebugRow label="Trend score" value={String(adv.trendScore)} />
          <DebugRow label="Heat score" value={String(adv.chainHeatScore)} />
          <DebugRow label="Bias (55/35/10)" value={String(adv.macroBiasScore)} />
          <DebugRow label="Val bucket" value={adv.valuationBucket} />
          <DebugRow label="Trend bucket" value={adv.trendBucket} />
          <DebugRow label="Bucket dist" value={String(adv.bucketDistance)} />
          <DebugRow label="MIXED rule" value={adv.mixedTriggered ? '✓ semantic conflict' : '✗ no conflict'} />
          <DebugRow label="Confidence" value={adv.confidence} />
        </DebugSection>

        {/* State section */}
        <DebugSection title="State">
          <DebugRow label="Network" value={bs.networkState} />
          <DebugRow label="Block" value={bs.blockState} />
          <DebugRow label="Price" value={bs.priceState} />
          <DebugRow label="Mood" value={bs.buddyMood} />
          <DebugRow label="Weather" value={bs.weather} />
          <DebugRow label="Heat" value={Number(bs.heatLevel).toFixed(3)} />
          <DebugRow label="Signature" value={bs.activeSignature ?? 'none'} />
          <DebugRow label="Transient" value={activeTransient ?? 'none'} />
          <DebugRow label="Tr count" value={String(transientDebug.triggerCount)} />
          <DebugRow label="Sentiment" value={bs.sentimentBucket ? `${bs.sentimentValue} (${bs.sentimentBucket})` : 'n/a'} />
        </DebugSection>

        {/* Copy section */}
        <DebugSection title="Copy">
          <DebugRow label="Verdict" value={bs.verdict} />
          <DebugRow label="Archetype" value={bs.dayArchetype} />
          <DebugRow label="Summary" value={bs.dailySummary} />
          <DebugRow label="Weekly" value={bs.weeklyVibe} />
        </DebugSection>

        {/* Meta section */}
        <DebugSection title="Meta">
          <DebugRow label="Snapshots" value={String(meta.snapshotCount)} />
          <DebugRow label="Worker" value={meta.workerStatus} />
          <DebugRow label="Stale sec" value={String(meta.staleSec)} />
          <DebugRow label="Updated" value={new Date(bs.updatedAt).toLocaleTimeString()} />
        </DebugSection>
      </div>
    </motion.div>
  );
}
